import React, { useState, useMemo, useCallback } from 'react';
import TerminationModal from './TerminationModal';
import { toast } from 'components/Toast';
import company from "assets/images/icon/company.png";
import companystatus from "assets/images/icon/company-status.png";
import onboard from "assets/images/icon/onboard.png";
import implementation from "assets/images/icon/implementation.png";
import Card from '../../components/Card';
import { Card as Bcard, Row, Col, Modal, Button } from "react-bootstrap";
import edit from 'assets/images/icon/edit-orange.png';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import { getEmployee, getEmployeeBankAccounts, saveI9Section2 } from 'services/employees';
import { getCompany } from 'services/company';
import PlaidLinkButton from 'components/PlaidLinkButton';
import { getEmployeeBenefits, getCompanyBenefits } from 'services/benefits';
import { getMiscDeductions, getChildSupportDeductions } from 'services/deductions';
import { getPayrolls, previewPayroll } from 'services/payrolls';
import { getBalances, createNotification } from 'services/timeOff';
import axiosInstance from '../../axios/axiosInstance';
import ENV from 'constants/env';
import routes from 'constants/routes';
import { stringMerger } from 'utils';

const _companyId = () => localStorage.getItem('companyId') || ENV.COMPANY_ID;
async function _downloadPaystubPdf(employeeId, payrollId, fileName) {
    const res = await axiosInstance.get(
        `/companies/${_companyId()}/employees/${employeeId}/paystubs/${payrollId}/download`,
        { responseType: 'blob' }
    );
    const contentType = res.headers['content-type'] || 'text/html';
    const isPdf = contentType.includes('pdf');
    const blob = new Blob([res.data], { type: contentType });
    const url  = URL.createObjectURL(blob);
    const dlName = fileName || `paystub-${payrollId}.pdf`;
    if (isPdf) {
        const a = document.createElement('a'); a.href = url; a.download = dlName;
        document.body.appendChild(a); a.click(); a.remove();
    } else {
        const tab = window.open(url, '_blank');
        if (!tab) { const a = document.createElement('a'); a.href = url; a.download = dlName; document.body.appendChild(a); a.click(); a.remove(); }
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
}

const ONBOARDING_API = 'https://onboarding-api.payrollnoot.com';

function ConfirmModal({ show, title, message, onConfirm, onCancel }) {
    if (!show) return null;
    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 16px',
        }}>
            <div style={{
                background: '#fff', borderRadius: 16, padding: '32px 28px 24px',
                maxWidth: 400, width: '100%', textAlign: 'center',
                boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            }}>
                <div style={{ fontSize: 44, marginBottom: 12 }}>🗑️</div>
                <h5 style={{ margin: '0 0 8px', fontWeight: 700, color: '#111827' }}>{title}</h5>
                <p style={{ margin: '0 0 24px', fontSize: 14, color: '#6b7280', lineHeight: 1.5 }}>{message}</p>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                    <button
                        onClick={onCancel}
                        className="btn btn-outline-secondary"
                        style={{ minWidth: 90, fontWeight: 600 }}
                    >Cancel</button>
                    <button
                        onClick={onConfirm}
                        className="btn btn-danger"
                        style={{ minWidth: 90, fontWeight: 600 }}
                    >Delete</button>
                </div>
            </div>
        </div>
    );
}

function formatDate(dateStr) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric', timeZone: 'UTC' });
}

function maskSSN(ssn) {
    if (!ssn) return '—';
    const clean = ssn.replace(/\D/g, '');
    return clean.length >= 4 ? `****** ${clean.slice(-4)}` : '******';
}

const TABS = ['Paystubs', 'Documents', 'Workplaces', 'Benefits', 'Misc DED', 'Time Off', 'Bank Accounts', 'I-9'];
const unwrap = (res) => Array.isArray(res) ? res : (res?.data ?? res?.items ?? []);
const fmtMoney = (v) => v != null ? `$${Number(v).toFixed(2)}` : '—';

const STUB_PRINT_ID = 'pay-stub-printable';

function handlePayStubPrint() {
    const el = document.getElementById(STUB_PRINT_ID);
    if (!el) return;
    const w = window.open('', '_blank', 'width=860,height=700');
    w.document.write(`<!DOCTYPE html><html><head><title>Pay Stub</title>
<style>
  body { font-family: Arial, sans-serif; font-size: 13px; color: #222; padding: 24px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
  td, th { padding: 5px 8px; }
  .text-end { text-align: right; }
  .text-danger { color: #dc3545; }
  .text-success { color: #198754; }
  .fw-bold { font-weight: 700; }
  .section-header { font-size: 13px; font-weight: 700; border-bottom: 2px solid #E7730A; padding-bottom: 4px; margin-bottom: 6px; }
  .stub-header { background: #fef9f5; border: 1px solid #f0d0b0; border-radius: 8px; padding: 12px 16px; display: flex; justify-content: space-between; margin-bottom: 16px; }
  .stub-header div { margin-right: 16px; }
  .stub-header .lbl { font-size: 10px; color: #888; text-transform: uppercase; letter-spacing: 1px; }
  .net-row { background: #f0faf0; border: 1px solid #c3e6c3; border-radius: 8px; padding: 12px 16px; text-align: right; margin-top: 12px; }
  .cols { display: flex; gap: 24px; }
  .col { flex: 1; }
  hr { border: none; border-top: 1px solid #dee2e6; margin: 10px 0; }
  .divider-row td { border-top: 2px solid #dee2e6; font-weight: 700; }
</style></head><body>`);
    w.document.write(el.innerHTML);
    w.document.write('</body></html>');
    w.document.close();
    w.focus();
    w.print();
    w.close();
}

function EmployeePayStubModal({ payroll, employeeId, onClose, emp, empBenefits, companyBenefitMap, empMisc, empChild, timeOffBalances, isLatestPayroll }) {
    const [downloading, setDownloading] = useState(false);
    const handleDownloadPdf = async () => {
        setDownloading(true);
        try {
            const name = emp ? [emp.firstName, emp.lastName].filter(Boolean).join('-') : 'employee';
            const date = payroll.payDate ? payroll.payDate.slice(0, 10) : payroll.id;
            await _downloadPaystubPdf(employeeId, payroll.id, `paystub-${name}-${date}.pdf`);
        }
        catch { toast('Failed to download PDF. Please try printing instead.', 'error'); }
        finally { setDownloading(false); }
    };
    const { data: calc, isLoading, isError } = useQuery({
        queryKey: ['preview', payroll.id],
        queryFn: () => previewPayroll(payroll.id),
    });

    const empData = useMemo(() => {
        if (!calc?.employees) return null;
        return calc.employees.find(e => e.employeeId === employeeId || e.id === employeeId);
    }, [calc, employeeId]);

    const period = `${formatDate(payroll.startDate)} \u2013 ${formatDate(payroll.endDate)}`;
    const payDate = formatDate(payroll.payDate);

    const totalTaxes = empData
        ? (empData.federalIncomeTax ?? 0) + (empData.stateIncomeTax ?? 0) + (empData.socialSecurityTax ?? 0)
          + (empData.medicareTax ?? 0) + (empData.additionalMedicareTax ?? 0) + (empData.localIncomeTax ?? 0)
        : 0;

    const totalDeductions = empData
        ? (empData.preTaxDeductions ?? 0) + (empData.postTaxDeductions ?? 0)
        : 0;

    const address = emp ? [emp.addressLine1, emp.city, emp.state, emp.zip].filter(Boolean).join(', ') : '';

    // Use snapshot benefit items (already period-converted) when available
    const snapshotBenefits = empData?.benefitItems ?? null;
    const getBenefitName = (b) => {
        const id = b.companyBenefitId ?? b.benefitId;
        const t = companyBenefitMap?.[id] || companyBenefitMap?.[String(id)] || {};
        return t.type || b.type || t.description || b.description || 'Benefit';
    };

    const allDeductionLines = snapshotBenefits
        ? snapshotBenefits.filter(b => (b.employeeAmount ?? 0) > 0).map((b, i) => ({
            key: `snap-${i}`,
            label: b.name,
            amount: b.employeeAmount,
          }))
        : [
            ...(empBenefits || []).filter(b => (b.employeeContributionAmount ?? b.employeeDeduction ?? 0) > 0).map(b => ({
                key: b.id,
                label: getBenefitName(b),
                amount: b.employeeContributionAmount ?? b.employeeDeduction,
            })),
            ...(empMisc || []).map(d => ({
                key: `misc-${d.id}`,
                label: d.description || 'Misc Deduction',
                amount: d.amount,
            })),
            ...(empChild || []).map(d => ({
                key: `child-${d.id}`,
                label: 'Child Support',
                amount: d.amount,
            })),
          ];

    return (
        <Modal show onHide={onClose} size="lg" centered>
            <Modal.Header closeButton style={{ background: '#E7730A', color: '#fff' }}>
                <Modal.Title style={{ fontSize: 16 }}>Pay Stub</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ fontSize: 13 }}>
                {isLoading && <p className="text-center text-muted py-4">Loading pay stub...</p>}
                {isError && <p className="text-center text-danger py-4">Failed to load pay stub data.</p>}
                {!isLoading && !isError && !empData && (
                    <p className="text-center text-muted py-4">No pay stub found for this employee in this payroll.</p>
                )}
                {empData && (
                    <div id={STUB_PRINT_ID}>
                        <div className="d-flex justify-content-between mb-3 p-3 flex-wrap gap-2"
                            style={{ background: '#fef9f5', borderRadius: 8, border: '1px solid #f0d0b0' }}>
                            <div>
                                <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Employee</div>
                                <div className="fw-bold">{empData.employeeFirstName ?? empData.firstName} {empData.employeeLastName ?? empData.lastName}</div>
                                {address && <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{address}</div>}
                            </div>
                            <div>
                                <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Pay Period</div>
                                <div className="fw-bold">{period}</div>
                            </div>
                            <div>
                                <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Pay Date</div>
                                <div className="fw-bold">{payDate}</div>
                            </div>
                            <div>
                                <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Pay Type</div>
                                <div className="fw-bold" style={{ textTransform: 'capitalize' }}>{empData.payType || '—'}</div>
                            </div>
                            {empData.paymentMethod && (
                                <div>
                                    <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Payment Method</div>
                                    <div className="fw-bold">{(empData.paymentMethod || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</div>
                                </div>
                            )}
                        </div>

                        <Row className="g-3">
                            <Col md={6}>
                                <div className="fw-bold mb-2" style={{ borderBottom: '2px solid #E7730A', paddingBottom: 4 }}>Earnings</div>
                                <table className="table table-sm mb-0">
                                    <tbody>
                                        <tr>
                                            <td>Gross Pay</td>
                                            <td className="text-end fw-bold">{fmtMoney(empData.grossPay)}</td>
                                        </tr>
                                        {(empData.regularHours ?? empData.hours) > 0 && (
                                            <tr><td className="text-muted ps-3" style={{ fontSize: 12 }}>Regular Hours ({empData.regularHours ?? empData.hours} hrs)</td><td className="text-end text-muted" style={{ fontSize: 12 }}>{fmtMoney(empData.regularPay ?? empData.regularWages)}</td></tr>
                                        )}
                                        {(empData.ptoHours ?? 0) > 0 && (
                                            <tr style={{ color: '#1565C0' }}><td className="ps-3" style={{ fontSize: 12 }}>PTO / Vacation ({empData.ptoHours} hrs)</td><td className="text-end" style={{ fontSize: 12 }}>{fmtMoney(empData.ptoPay ?? empData.ptoWages)}</td></tr>
                                        )}
                                        {(empData.sickHours ?? 0) > 0 && (
                                            <tr style={{ color: '#7B1FA2' }}><td className="ps-3" style={{ fontSize: 12 }}>Sick Leave ({empData.sickHours} hrs)</td><td className="text-end" style={{ fontSize: 12 }}>{fmtMoney(empData.sickPay ?? empData.sickWages)}</td></tr>
                                        )}
                                        {(empData.paidHolidayHours ?? 0) > 0 && (
                                            <tr style={{ color: '#2E7D32' }}><td className="ps-3" style={{ fontSize: 12 }}>Holiday ({empData.paidHolidayHours} hrs)</td><td className="text-end" style={{ fontSize: 12 }}>{fmtMoney(empData.holidayPay ?? empData.paidHolidayWages)}</td></tr>
                                        )}
                                        {(empData.overtimeHours ?? 0) > 0 && (
                                            <tr style={{ color: '#BF360C' }}><td className="ps-3" style={{ fontSize: 12 }}>Overtime ({empData.overtimeHours} hrs @ 1.5x)</td><td className="text-end" style={{ fontSize: 12 }}>{fmtMoney(empData.overtimePay ?? empData.overtimeWages)}</td></tr>
                                        )}
                                        {(empData.bonusAmount ?? empData.bonus ?? 0) > 0 && <tr><td className="ps-3" style={{ fontSize: 12 }}>{payroll?.type === 'correction' ? 'Correction Adjustment' : 'Bonus'}</td><td className="text-end" style={{ fontSize: 12 }}>{fmtMoney(empData.bonusAmount ?? empData.bonus)}</td></tr>}
                                        {(empData.commissionAmount ?? empData.commission ?? 0) > 0 && <tr><td className="ps-3" style={{ fontSize: 12 }}>Commission</td><td className="text-end" style={{ fontSize: 12 }}>{fmtMoney(empData.commissionAmount ?? empData.commission)}</td></tr>}
                                        {(empData.reimbursementAmount ?? empData.reimbursement ?? 0) > 0 && <tr><td className="ps-3" style={{ fontSize: 12 }}>Reimbursement</td><td className="text-end" style={{ fontSize: 12 }}>{fmtMoney(empData.reimbursementAmount ?? empData.reimbursement)}</td></tr>}
                                    </tbody>
                                </table>
                                <div className="mt-2" style={{ borderTop: "1px solid #dee2e6", paddingTop: 8 }}>
                                    <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
                                        PTO / Time Off Balances
                                        {isLatestPayroll && <span style={{ fontSize: 10, color: "#aaa", marginLeft: 6, textTransform: "none" }}>(current)</span>}
                                    </div>
                                    {isLatestPayroll && timeOffBalances && timeOffBalances.length > 0 ? (
                                        <table className="table table-sm mb-0">
                                            <thead style={{ fontSize: 11, color: "#888" }}>
                                                <tr>
                                                    <th style={{ fontWeight: 500 }}>Policy</th>
                                                    <th className="text-end" style={{ fontWeight: 500 }}>Available</th>
                                                    <th className="text-end" style={{ fontWeight: 500 }}>Used YTD</th>
                                                    <th className="text-end" style={{ fontWeight: 500 }}>Pending</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {timeOffBalances.map(b => (
                                                    <tr key={b.id}>
                                                        <td style={{ fontSize: 12, color: "#555" }}>{b.policy?.name ?? b.policy?.timeOffType ?? "PTO"}</td>
                                                        <td className="text-end" style={{ fontSize: 12, color: "#1565C0", fontWeight: 600 }}>{b.balanceHours ?? 0} hrs</td>
                                                        <td className="text-end" style={{ fontSize: 12, color: "#dc3545" }}>{b.usedHours ?? 0} hrs</td>
                                                        <td className="text-end" style={{ fontSize: 12, color: "#fd7e14" }}>{b.pendingHours ?? 0} hrs</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <p style={{ fontSize: 12, color: "#aaa", marginBottom: 0 }}>
                                            {isLatestPayroll ? "No time off policies assigned." : "Balance history is not available for historical paystubs."}
                                        </p>
                                    )}
                                </div>
                            </Col>

                            <Col md={6}>
                                <div className="fw-bold mb-2" style={{ borderBottom: '2px solid #dc3545', paddingBottom: 4 }}>Deductions &amp; Taxes</div>
                                <table className="table table-sm mb-0">
                                    <tbody>
                                        {allDeductionLines.map(line => (
                                            <tr key={line.key}>
                                                <td className="text-muted" style={{ fontSize: 12 }}>{line.label}</td>
                                                <td className="text-end text-danger" style={{ fontSize: 12 }}>{fmtMoney(line.amount)}</td>
                                            </tr>
                                        ))}
                                        {empData.federalIncomeTax > 0 && (
                                            <tr><td>Federal Income Tax (FITW)</td><td className="text-end" style={{ color: '#fd7e14' }}>{fmtMoney(empData.federalIncomeTax)}</td></tr>
                                        )}
                                        {empData.stateIncomeTax > 0 && (
                                            <tr><td>State Income Tax (SIT)</td><td className="text-end" style={{ color: '#fd7e14' }}>{fmtMoney(empData.stateIncomeTax)}</td></tr>
                                        )}
                                        {empData.socialSecurityTax > 0 && (
                                            <tr><td>Social Security Tax</td><td className="text-end" style={{ color: '#fd7e14' }}>{fmtMoney(empData.socialSecurityTax)}</td></tr>
                                        )}
                                        {empData.medicareTax > 0 && (
                                            <tr><td>Medicare Tax</td><td className="text-end" style={{ color: '#fd7e14' }}>{fmtMoney(empData.medicareTax)}</td></tr>
                                        )}
                                        {(empData.additionalMedicareTax ?? 0) > 0 && (
                                            <tr><td>Additional Medicare (0.9%)</td><td className="text-end" style={{ color: '#fd7e14' }}>{fmtMoney(empData.additionalMedicareTax)}</td></tr>
                                        )}
                                        {(empData.localIncomeTax ?? 0) > 0 && (
                                            <tr><td>Local / City Tax</td><td className="text-end" style={{ color: '#fd7e14' }}>{fmtMoney(empData.localIncomeTax)}</td></tr>
                                        )}
                                        <tr style={{ borderTop: '1px solid #dee2e6' }}>
                                            <td className="fw-bold">Total Taxes</td>
                                            <td className="text-end fw-bold" style={{ color: '#fd7e14' }}>{fmtMoney(totalTaxes)}</td>
                                        </tr>
                                        <tr>
                                            <td className="fw-bold">Total Deductions</td>
                                            <td className="text-end fw-bold text-danger">{fmtMoney(totalDeductions)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-between align-items-center p-3 mt-3"
                            style={{ background: '#f0faf0', borderRadius: 8, border: '1px solid #c3e6c3' }}>
                            <div style={{ fontSize: 12, color: '#555' }}>
                                Gross Pay {fmtMoney(empData.grossPay)}
                                &nbsp;&minus;&nbsp; Deductions {fmtMoney(totalDeductions)}
                                &nbsp;&minus;&nbsp; Taxes {fmtMoney(totalTaxes)}
                            </div>
                            <div>
                                <span className="text-muted me-2" style={{ fontSize: 13 }}>Net Pay</span>
                                <span className="fw-bold" style={{ fontSize: 22, color: '#2e7d32' }}>{fmtMoney(empData.netPay)}</span>
                            </div>
                        </div>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" size="sm" onClick={onClose}>Close</Button>
                {empData && (
                    <Button size="sm" style={{ backgroundColor: '#1a9fbb', border: 'none' }} onClick={handleDownloadPdf} disabled={downloading}>
                        {downloading ? 'Downloading...' : 'Download PDF'}
                    </Button>
                )}
                {empData && (
                    <Button size="sm" style={{ backgroundColor: '#E7730A', border: 'none' }} onClick={handlePayStubPrint}>
                        Print
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
}

const I9_STORAGE_KEY = (employeeId) => `i9_section2_${employeeId}`;

const LIST_A_DOCS = [
    'U.S. Passport',
    'U.S. Passport Card',
    'Permanent Resident Card (Form I-551)',
    'Foreign Passport with Temporary I-551 Stamp',
    'Employment Authorization Document (Form I-766)',
    'Passport with Form I-94 (nonimmigrant)',
    'Passport from Federated States of Micronesia or Marshall Islands',
];
const LIST_B_DOCS = [
    'Driver\'s License or ID Card',
    'ID Card issued by federal, state, or local government',
    'School ID Card with photo',
    'Voter\'s Registration Card',
    'U.S. Military Card or Draft Record',
    'Military Dependent\'s ID Card',
    'U.S. Coast Guard Merchant Mariner Card',
    'Native American Tribal Document',
    'Driver\'s License issued by a Canadian government authority',
];
const LIST_C_DOCS = [
    'U.S. Social Security Account Number Card',
    'Certification of Report of Birth (Form DS-1350)',
    'Original or Certified Birth Certificate',
    'Native American Tribal Document',
    'U.S. Citizen ID Card (Form I-197)',
    'ID Card for use of Resident Citizen (Form I-179)',
    'Employment Authorization Document (not Form I-766)',
];

const I9_PRINT_ID = 'i9-printable-section';

function printI9(emp, sec2) {
    const w = window.open('', '_blank', 'width=900,height=700');
    const eName = [emp.firstName, emp.lastName].filter(Boolean).join(' ');
    const eAddr = [emp.addressLine1 || emp.address, emp.city, emp.state, emp.zip].filter(Boolean).join(', ');
    w.document.write(`<!DOCTYPE html><html><head><title>Form I-9 — ${eName}</title>
<style>
  body { font-family: Arial, sans-serif; font-size: 12px; color: #000; padding: 20px; max-width: 800px; margin: 0 auto; }
  h2 { text-align:center; font-size:15px; border-bottom:2px solid #000; padding-bottom:6px; }
  h3 { font-size:13px; background:#e0e0e0; padding:4px 8px; margin:16px 0 8px; }
  table { width:100%; border-collapse:collapse; font-size:12px; }
  td, th { border:1px solid #999; padding:5px 8px; }
  th { background:#f0f0f0; text-align:left; }
  .label { font-size:10px; color:#555; display:block; }
  .val { font-weight:600; }
  .disclaimer { font-size:10px; color:#555; margin-top:20px; padding-top:8px; border-top:1px solid #ccc; }
  .sig-line { border-bottom:1px solid #000; min-height:30px; margin:12px 0 4px; }
  .row2 { display:flex; gap:20px; }
  .col { flex:1; }
</style></head><body>
<h2>Employment Eligibility Verification — FORM I-9<br/><span style="font-size:11px;">U.S. Department of Homeland Security</span></h2>

<h3>SECTION 1 — Employee Information and Attestation</h3>
<table>
  <tr><th>Last Name</th><th>First Name</th><th>Middle Name</th></tr>
  <tr><td>${emp.lastName || ''}</td><td>${emp.firstName || ''}</td><td>${emp.middleName || ''}</td></tr>
  <tr><th>Address</th><th>Date of Birth</th><th>SSN (last 4)</th></tr>
  <tr><td>${eAddr}</td><td>${emp.dateOfBirth ? new Date(emp.dateOfBirth).toLocaleDateString() : ''}</td>
      <td>${emp.ssn ? '•••-••-' + String(emp.ssn).slice(-4) : ''}</td></tr>
  <tr><th colspan="3">Attestation</th></tr>
  <tr><td colspan="3">
    ${emp.usCitizen === 'yes' || emp.usCitizen === 'citizen' ? '☑' : '☐'} A citizen of the United States<br/>
    ${emp.authorizedToWork === 'lawful_permanent_resident' ? '☑' : '☐'} A lawful permanent resident<br/>
    ${emp.authorizedToWork === 'alien_authorized_to_work' || emp.workAuthType ? '☑' : '☐'} An alien authorized to work
    ${emp.workAuthExpiry ? ' (expires: ' + new Date(emp.workAuthExpiry).toLocaleDateString() + ')' : ''}
  </td></tr>
  <tr><th>I-9 Signed Date</th><th>Section 1 Status</th><th></th></tr>
  <tr><td>${emp.i9Date ? new Date(emp.i9Date).toLocaleDateString() : '—'}</td>
      <td>${emp.i9Section1Completed || '—'}</td><td></td></tr>
</table>

<h3>SECTION 2 — Employer Review and Verification</h3>
${sec2.listType === 'A' ? `
<table>
  <tr><th colspan="3">List A — Identity and Employment Authorization</th></tr>
  <tr><th>Document Title</th><th>Issuing Authority</th><th>Document Number</th></tr>
  <tr><td>${sec2.listATitle || ''}</td><td>${sec2.listAAuthority || ''}</td><td>${sec2.listANumber || ''}</td></tr>
  <tr><th>Expiration Date</th><td colspan="2">${sec2.listAExpiry || 'N/A'}</td></tr>
</table>
` : `
<table>
  <tr><th colspan="3">List B — Identity</th></tr>
  <tr><th>Document Title</th><th>Issuing Authority</th><th>Document Number</th></tr>
  <tr><td>${sec2.listBTitle || ''}</td><td>${sec2.listBAuthority || ''}</td><td>${sec2.listBNumber || ''}</td></tr>
  <tr><th>Expiration Date</th><td colspan="2">${sec2.listBExpiry || 'N/A'}</td></tr>
</table>
<table style="margin-top:8px">
  <tr><th colspan="3">List C — Employment Authorization</th></tr>
  <tr><th>Document Title</th><th>Issuing Authority</th><th>Document Number</th></tr>
  <tr><td>${sec2.listCTitle || ''}</td><td>${sec2.listCAuthority || ''}</td><td>${sec2.listCNumber || ''}</td></tr>
  <tr><th>Expiration Date</th><td colspan="2">${sec2.listCExpiry || 'N/A'}</td></tr>
</table>
`}
<table style="margin-top:12px">
  <tr><th>Employee's First Day of Employment</th><th>Verified by (Name & Title)</th></tr>
  <tr><td>${sec2.firstDay || ''}</td><td>${sec2.verifierName || ''}, ${sec2.verifierTitle || ''}</td></tr>
  <tr><th>Employer Business Name</th><th>Employer Address</th></tr>
  <tr><td>${sec2.employerName || ''}</td><td>${sec2.employerAddress || ''}</td></tr>
  <tr><th>Date of Verification</th><td>${sec2.verifiedDate || ''}</td></tr>
</table>

<p class="disclaimer">
  Form I-9 is for employment eligibility verification in the United States.<br/>
  Employers must retain this form for three years after the date of hire or for one year after employment ends, whichever is later.
</p>
</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
}

function I9Section2Tab({ emp, queryClient }) {
    const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD
    const { data: company } = useQuery({ queryKey: ['company'], queryFn: getCompany });

    const initSec2 = () => {
        if (emp.i9Section2Json) {
            try { return JSON.parse(emp.i9Section2Json); } catch {}
        }
        // migrate from localStorage if exists
        try {
            const ls = localStorage.getItem(I9_STORAGE_KEY(emp.id));
            if (ls) return JSON.parse(ls);
        } catch {}
        return {};
    };

    const [sec2, setSec2] = useState(initSec2);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [saveError, setSaveError] = useState(null);

    // Auto-fill employer fields from company + employee data (only if not already set)
    React.useEffect(() => {
        if (!company) return;
        setSec2(prev => ({
            ...prev,
            firstDay: prev.firstDay || (emp.startDate ? new Date(emp.startDate).toLocaleDateString('en-CA') : ''),
            verifiedDate: prev.verifiedDate || today,
            employerName: prev.employerName || company?.legalName || '',
            employerAddress: prev.employerAddress || [company?.addressLine1, company?.city, company?.state, company?.zip].filter(Boolean).join(', ') || '',
        }));
    }, [company?.legalName]);

    const set = (field) => (e) => setSec2(prev => ({ ...prev, [field]: e.target.value }));

    const handleSave = async () => {
        setSaving(true); setSaveError(null);
        try {
            await saveI9Section2(emp.id, sec2);
            localStorage.removeItem(I9_STORAGE_KEY(emp.id)); // clean up old localStorage entry
            queryClient.invalidateQueries({ queryKey: ['employee', emp.id] });
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch (err) {
            setSaveError('Failed to save. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const fld = (label, field, opts) => (
        <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 3 }}>{label}</label>
            {opts ? (
                <select
                    className="form-select form-select-sm"
                    value={sec2[field] || ''}
                    onChange={set(field)}
                >
                    <option value="">— Select —</option>
                    {opts.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
            ) : (
                <input
                    type="text"
                    className="form-control form-control-sm"
                    value={sec2[field] || ''}
                    onChange={set(field)}
                />
            )}
        </div>
    );

    return (
        <div style={{ fontSize: 13 }}>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <div>
                    <h6 className="fw-bold mb-0">I-9 Employment Eligibility Verification</h6>
                    <small className="text-muted">Section 1 is pre-filled from onboarding. Complete Section 2 below.</small>
                </div>
                <div className="d-flex gap-2 align-items-center">
                    {saveError && <span style={{ color: '#dc2626', fontSize: 12 }}>{saveError}</span>}
                    {emp.i9Section2CompletedAt && !saved && (
                        <span style={{ fontSize: 11, color: '#16a34a' }}>
                            ✓ Last saved {new Date(emp.i9Section2CompletedAt).toLocaleDateString()}
                        </span>
                    )}
                    <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Section 2'}
                    </button>
                    <button
                        className="btn btn-sm btn-warning fw-semibold"
                        style={{ background: '#E7730A', color: '#fff', border: 'none' }}
                        onClick={() => printI9(emp, sec2)}
                    >
                        Print / Download I-9
                    </button>
                </div>
            </div>

            {/* Section 1 summary */}
            <div className="p-3 rounded mb-3" style={{ background: '#f8f9fa', border: '1px solid #dee2e6' }}>
                <p className="fw-semibold mb-2" style={{ fontSize: 13 }}>Section 1 — Employee Attestation (from onboarding)</p>
                <div className="row g-2" style={{ fontSize: 12 }}>
                    <div className="col-md-3"><span className="text-muted">US Citizen:</span> <strong>{emp.usCitizen || '—'}</strong></div>
                    <div className="col-md-3"><span className="text-muted">Auth to Work:</span> <strong>{emp.authorizedToWork || '—'}</strong></div>
                    <div className="col-md-3"><span className="text-muted">Auth Type:</span> <strong>{emp.workAuthType || '—'}</strong></div>
                    <div className="col-md-3"><span className="text-muted">Auth Expiry:</span> <strong>{emp.workAuthExpiry ? new Date(emp.workAuthExpiry).toLocaleDateString() : '—'}</strong></div>
                    <div className="col-md-3"><span className="text-muted">I-9 Signed:</span> <strong>{emp.i9Date ? new Date(emp.i9Date).toLocaleDateString() : '—'}</strong></div>
                    <div className="col-md-3"><span className="text-muted">Section 1 Status:</span> <strong>{emp.i9Section1Completed || '—'}</strong></div>
                </div>
            </div>

            {/* Section 2 — Document type selection */}
            <div className="p-3 rounded mb-3" style={{ background: '#fff', border: '1px solid #dee2e6' }}>
                <p className="fw-semibold mb-3" style={{ fontSize: 13 }}>Section 2 — Employer or Authorized Representative Review and Verification</p>

                <div className="mb-3">
                    <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 }}>Document List</label>
                    <div className="d-flex gap-3">
                        {['A', 'BC'].map(l => (
                            <label key={l} style={{ cursor: 'pointer', fontSize: 13 }}>
                                <input
                                    type="radio"
                                    name="listType"
                                    value={l}
                                    checked={(sec2.listType || 'A') === l}
                                    onChange={set('listType')}
                                    style={{ marginRight: 6 }}
                                />
                                {l === 'A' ? 'List A (Identity + Work Auth)' : 'List B + C (Identity + Work Auth separately)'}
                            </label>
                        ))}
                    </div>
                </div>

                {(sec2.listType || 'A') === 'A' ? (
                    <div className="row g-3">
                        <div className="col-md-6">{fld('List A — Document Title', 'listATitle', LIST_A_DOCS)}</div>
                        <div className="col-md-3">{fld('Issuing Authority', 'listAAuthority')}</div>
                        <div className="col-md-3">{fld('Document Number', 'listANumber')}</div>
                        <div className="col-md-3">{fld('Expiration Date (N/A if none)', 'listAExpiry')}</div>
                    </div>
                ) : (
                    <>
                        <p className="text-muted mb-2" style={{ fontSize: 12 }}>List B — Identity Document</p>
                        <div className="row g-3 mb-3">
                            <div className="col-md-6">{fld('List B — Document Title', 'listBTitle', LIST_B_DOCS)}</div>
                            <div className="col-md-3">{fld('Issuing Authority', 'listBAuthority')}</div>
                            <div className="col-md-3">{fld('Document Number', 'listBNumber')}</div>
                            <div className="col-md-3">{fld('Expiration Date (N/A if none)', 'listBExpiry')}</div>
                        </div>
                        <p className="text-muted mb-2" style={{ fontSize: 12 }}>List C — Employment Authorization Document</p>
                        <div className="row g-3">
                            <div className="col-md-6">{fld('List C — Document Title', 'listCTitle', LIST_C_DOCS)}</div>
                            <div className="col-md-3">{fld('Issuing Authority', 'listCAuthority')}</div>
                            <div className="col-md-3">{fld('Document Number', 'listCNumber')}</div>
                            <div className="col-md-3">{fld('Expiration Date (N/A if none)', 'listCExpiry')}</div>
                        </div>
                    </>
                )}
            </div>

            {/* Employer Certification */}
            <div className="p-3 rounded" style={{ background: '#fff', border: '1px solid #dee2e6' }}>
                <p className="fw-semibold mb-3" style={{ fontSize: 13 }}>Employer Certification</p>
                <div className="row g-3">
                    <div className="col-md-4">{fld("Employee's First Day of Employment", 'firstDay')}</div>
                    <div className="col-md-4">{fld('Date of Verification', 'verifiedDate')}</div>
                    <div className="col-md-4">{fld('Verifier Name & Title', 'verifierName')}</div>
                    <div className="col-md-4">{fld('Verifier Title', 'verifierTitle')}</div>
                    <div className="col-md-4">{fld('Employer Business Name', 'employerName')}</div>
                    <div className="col-md-4">{fld('Employer Address', 'employerAddress')}</div>
                </div>
            </div>
        </div>
    );
}

function ViewEmployees() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const employeeId = searchParams.get('code');
    const companyId = localStorage.getItem('companyId') || '';
    const TAB_URL_MAP = { paystubs: 0, documents: 1, workplaces: 2, benefits: 3, 'misc-ded': 4, 'time-off': 5, 'bank-accounts': 6, 'i-9': 7 };
    const [activeTab, setActiveTab] = useState(() => TAB_URL_MAP[searchParams.get('tab')] ?? 0);
    const [stubPayroll, setStubPayroll] = useState(null);
    const [showTermModal, setShowTermModal] = useState(false);
    const [empDocs, setEmpDocs] = useState(null);       // null=not loaded, []=empty, [...]= docs
    const [docsLoading, setDocsLoading] = useState(false);
    const [deletingDocId, setDeletingDocId] = useState(null);
    const [empTaxDocs, setEmpTaxDocs] = useState(null);  // W-2 / 1099 from TaxGeneratedDocuments
    const [taxDocsLoading, setTaxDocsLoading] = useState(false);
    const [confirmModal, setConfirmModal] = useState({ show: false, docId: null });
    const [uploadingDoc, setUploadingDoc] = useState(false);
    const docUploadRef = React.useRef(null);

    const { data: emp, isLoading, isError } = useQuery({
        queryKey: ['employee', employeeId],
        queryFn: () => getEmployee(employeeId),
        enabled: !!employeeId,
    });

    const { data: empBankAccountsRaw } = useQuery({
        queryKey: ['employee-bank-accounts', employeeId],
        queryFn: () => getEmployeeBankAccounts(employeeId),
        enabled: !!employeeId,
        staleTime: 5 * 60 * 1000,
    });
    const empBankAccounts = Array.isArray(empBankAccountsRaw) ? empBankAccountsRaw : (empBankAccountsRaw?.data ?? empBankAccountsRaw?.items ?? []);

    const { data: benefitsRaw } = useQuery({ queryKey: ['employee-benefits'], queryFn: getEmployeeBenefits });
    const { data: companyBenefitsRaw } = useQuery({ queryKey: ['company-benefits'], queryFn: getCompanyBenefits });

    const companyBenefitMap = useMemo(() => {
        const map = {};
        unwrap(companyBenefitsRaw).forEach(b => { map[b.id] = b; });
        return map;
    }, [companyBenefitsRaw]);
    const { data: miscRaw } = useQuery({ queryKey: ['misc-deductions'], queryFn: getMiscDeductions });
    const { data: childRaw } = useQuery({ queryKey: ['child-support-deductions'], queryFn: getChildSupportDeductions });
    const { data: payrollsRaw } = useQuery({ queryKey: ['payrolls'], queryFn: getPayrolls });
    const { data: timeOffBalancesRaw } = useQuery({
        queryKey: ['time-off-balances-emp', employeeId],
        queryFn: () => getBalances(employeeId),
        enabled: !!employeeId,
        retry: false,
        throwOnError: false,
    });

    const empBenefits = useMemo(() => unwrap(benefitsRaw).filter(b => b.employeeId === employeeId), [benefitsRaw, employeeId]);
    const empMisc = useMemo(() => unwrap(miscRaw).filter(d => d.employeeId === employeeId), [miscRaw, employeeId]);
    const empChild = useMemo(() => unwrap(childRaw).filter(d => d.employeeId === employeeId), [childRaw, employeeId]);
    const approvedPayrolls = useMemo(() => unwrap(payrollsRaw).filter(p => p.status === 'approved'), [payrollsRaw]);
    const timeOffBalances = useMemo(() => Array.isArray(timeOffBalancesRaw) ? timeOffBalancesRaw : [], [timeOffBalancesRaw]);

    const payrollDetailQueries = useQueries({
        queries: activeTab === 0 ? approvedPayrolls.map(p => ({
            queryKey: ['preview', p.id],
            queryFn: () => previewPayroll(p.id),
            staleTime: 5 * 60 * 1000,
        })) : [],
    });

    const payrollsChecking = activeTab === 0 && payrollDetailQueries.some(q => q.isLoading);
    const employeePayrolls = useMemo(() => {
        if (activeTab !== 0 || payrollsChecking) return [];
        return approvedPayrolls.filter((_, i) => {
            const preview = payrollDetailQueries[i]?.data;
            if (!preview?.employees) return false;
            return preview.employees.some(e => e.employeeId === employeeId || e.id === employeeId);
        });
    }, [approvedPayrolls, payrollDetailQueries, employeeId, activeTab, payrollsChecking]);

    const fetchDocs = useCallback(async (email) => {
        if (!email) return;
        setDocsLoading(true);
        try {
            const token = localStorage.getItem('authToken') || localStorage.getItem('token') || localStorage.getItem('accessToken');
            const resp  = await fetch(`${ONBOARDING_API}/api/employee-documents?email=${encodeURIComponent(email)}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await resp.json();
            setEmpDocs(Array.isArray(data) ? data : []);
        } catch { setEmpDocs([]); }
        finally { setDocsLoading(false); }
    }, []);

    const fetchTaxDocs = useCallback(async (empId) => {
        if (!empId) return;
        const cid = localStorage.getItem('companyId') || '';
        if (!cid) return;
        setTaxDocsLoading(true);
        try {
            const res = await axiosInstance.get(
                `/companies/${cid}/documents/employee_tax_documents?employeeId=${empId}`
            );
            const list = res.data?.data ?? res.data ?? [];
            setEmpTaxDocs(Array.isArray(list) ? list : []);
        } catch { setEmpTaxDocs([]); }
        finally { setTaxDocsLoading(false); }
    }, []);

    const deleteDoc = (docId) => {
        setConfirmModal({ show: true, docId });
    };

    const confirmDeleteDoc = async () => {
        const docId = confirmModal.docId;
        setConfirmModal({ show: false, docId: null });
        setDeletingDocId(docId);
        try {
            const token = localStorage.getItem('authToken') || localStorage.getItem('token') || localStorage.getItem('accessToken');
            await fetch(`${ONBOARDING_API}/api/employee-documents/${docId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            });
            setEmpDocs(prev => prev.filter(d => d.id !== docId));
        } catch { toast('Failed to delete document.', 'error'); }
        finally { setDeletingDocId(null); }
    };

    const uploadDoc = async (file) => {
        if (!file || !emp?.email) return;
        const allowed = ['.pdf', '.doc', '.docx', '.csv', '.png', '.jpg', '.jpeg'];
        const ext = '.' + file.name.split('.').pop().toLowerCase();
        if (!allowed.includes(ext)) {
            toast('File type not allowed. Accepted: PDF, DOC, DOCX, CSV, PNG, JPG.', 'warning');
            return;
        }
        setUploadingDoc(true);
        try {
            const token = localStorage.getItem('authToken') || localStorage.getItem('token') || localStorage.getItem('accessToken');
            const formData = new FormData();
            formData.append('file', file);
            const resp = await fetch(
                `${ONBOARDING_API}/api/employee-documents/upload?email=${encodeURIComponent(emp.email)}`,
                { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData }
            );
            if (resp.ok) {
                const doc = await resp.json();
                setEmpDocs(prev => [doc, ...(prev || [])]);
                // Notify the employee a new document is available
                const empId = emp?.id;
                const cid = localStorage.getItem('companyId');
                if (empId && cid) {
                    const docLabel = doc?.label || doc?.name || doc?.documentType || 'document';
                    createNotification({
                        recipientId: empId,
                        recipientType: 'employee',
                        type: 'document_uploaded',
                        title: 'New Document Available',
                        message: `A new document (${docLabel}) has been uploaded to your profile. You can view it in your Documents section.`,
                        link: '/employee/documents?tab=my-documents',
                        companyId: cid,
                    }).catch(() => {});
                }
            } else {
                const err = await resp.json().catch(() => ({}));
                toast(err.message || 'Upload failed. Please try again.', 'error');
            }
        } catch { toast('Upload failed. Please check your connection.', 'error'); }
        finally { setUploadingDoc(false); if (docUploadRef.current) docUploadRef.current.value = ''; }
    };

    if (isLoading) {
        return <div className="content" style={{ padding: '16px 24px' }}><p>Loading employee...</p></div>;
    }

    if (isError || !emp) {
        return <div className="content" style={{ padding: '16px 24px' }}><p className="text-danger">Failed to load employee data.</p></div>;
    }

    const fullName = [emp.firstName, emp.middleName, emp.lastName].filter(Boolean).join(' ') || '—';
    const residence = [emp.addressLine1, emp.city, emp.state, emp.zip].filter(Boolean).join(', ') || '—';

    const userDetail = [
        { title: 'Gross Pay', count: emp.grossPay ?? 0, icon: company },
        { title: 'Reimbursements', count: emp.reimbursements ?? 0, icon: companystatus },
        { title: 'Company Benefits', count: emp.benefitsCount ?? 0, icon: onboard },
        { title: 'Company Taxes', count: emp.taxesCount ?? 0, icon: implementation },
    ];

    const onboardStatusRaw = emp.onboardingStatus || emp.status || emp.onboardStatus || '';
    const onboardStatus = (() => {
        if (['approved', 'active', 'completed'].includes(onboardStatusRaw?.toLowerCase())) return 'Approved';
        if (onboardStatusRaw?.toLowerCase() === 'pending_review') return 'Pending Review';
        if (onboardStatusRaw?.toLowerCase() === 'rejected') return 'Rejected';
        return onboardStatusRaw || '—';
    })();
    const activeStatus = emp.active ? 'Active' : 'Inactive';

    // New unified onboarding approval workflow
    const isPendingReview = emp.onboardingStatus === 'pending_review';

    // Show banner when employee completed self-onboarding but admin fields are not yet filled.
    // emp.payRate is the flat field returned by the API (wage.rate is the nested write form).
    const onboardDone = ['completed', 'Completed'].includes(emp.onboardStatus || emp.status || '');
    const needsAdminSetup = !isPendingReview && onboardDone
        && (!emp.payRate || Number(emp.payRate) === 0);

    return (
        <div className='content' style={{ padding: "16px 24px 16px 16px" }}>
            <div className="start-new-payroll-section">
                <div>
                    <div className="row mt-4">
                        {userDetail.map((item) => (
                            <Card key={item.title} title={item.title} count={item.count} icon={item.icon} />
                        ))}
                    </div>
                </div>

                {/* Pending Review banner — shown when onboardingStatus = pending_review */}
                {isPendingReview && (
                    <div style={{
                        display:'flex', alignItems:'flex-start', gap:14,
                        background:'#fff7ed', border:'2px solid #E7730A',
                        borderRadius:12, padding:'14px 18px', marginTop:16,
                    }}>
                        <span style={{ fontSize:22, lineHeight:1 }}>👤</span>
                        <div style={{ flex:1 }}>
                            <div style={{ fontWeight:700, color:'#c2410c', fontSize:14, marginBottom:3 }}>
                                Pending Onboarding Approval
                            </div>
                            <div style={{ fontSize:13, color:'#7c2d12', lineHeight:1.6 }}>
                                This employee completed self-onboarding and is awaiting your review. They are currently <strong>excluded from payroll</strong> until approved.
                            </div>
                        </div>
                        <button
                            onClick={() => navigate(routes.ONBOARDING.HOME)}
                            style={{ fontSize:13, color:'#fff', fontWeight:700, whiteSpace:'nowrap', alignSelf:'center', background:'#E7730A', border:'none', borderRadius:20, padding:'8px 18px', cursor:'pointer' }}
                        >
                            Review &amp; Approve →
                        </button>
                    </div>
                )}

                {/* Incomplete Setup banner — shown after employee self-onboards, before admin finalizes */}
                {needsAdminSetup && (
                    <div style={{
                        display:'flex', alignItems:'flex-start', gap:14,
                        background:'#fff7ed', border:'2px solid #f97316',
                        borderRadius:12, padding:'14px 18px', marginTop:16,
                    }}>
                        <span style={{ fontSize:22, lineHeight:1 }}>⚙️</span>
                        <div style={{ flex:1 }}>
                            <div style={{ fontWeight:700, color:'#c2410c', fontSize:14, marginBottom:3 }}>
                                Incomplete Setup — Admin Action Required
                            </div>
                            <div style={{ fontSize:13, color:'#7c2d12', lineHeight:1.6 }}>
                                This employee completed self-onboarding. Please finalize the following fields before running payroll:
                            </div>
                            <div style={{ display:'flex', flexWrap:'wrap', gap:'6px 16px', marginTop:8 }}>
                                {[
                                    { label:'Wage / Pay rate',    ok: !!(emp.payRate && Number(emp.payRate) > 0) },
                                    { label:'Pay type',           ok: !!emp.payType },
                                    { label:'FLSA exempt status', ok: !!(emp.flsaOvertimeExemptionStatus) },
                                    { label:'Work location',      ok: !!(emp.workplaceId || emp.workLocation) },
                                    { label:'Active status',      ok: !!emp.active },
                                ].map(({ label, ok }) => (
                                    <span key={label} style={{
                                        fontSize:12, fontWeight:600, padding:'3px 10px',
                                        borderRadius:20, border:'1.5px solid',
                                        borderColor: ok ? '#16a34a' : '#f97316',
                                        color: ok ? '#15803d' : '#c2410c',
                                        background: ok ? '#f0fdf4' : '#fff7ed',
                                    }}>
                                        {ok ? '✓' : '○'} {label}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <button
                            onClick={() => navigate(`${stringMerger(routes.EMPLOYEES.HOME, routes.EMPLOYEES.ADD_EDIT_EMPLOYEE)}?id=${emp.id}&highlight=setup`)}
                            style={{ fontSize:13, color:'#f97316', fontWeight:700, whiteSpace:'nowrap', alignSelf:'center', background:'#fff7ed', border:'1.5px solid #f97316', borderRadius:20, padding:'6px 14px', cursor:'pointer' }}
                        >
                            ✏️ Complete Setup →
                        </button>
                    </div>
                )}

                <div className='d-flex flex-row gap-4 mt-3 view-page-layout'>
                    <Bcard className="employee-card p-4" style={{ width: '100%' }}>
                        <Row className="align-items-center">
                            <Col>
                                <small className="text-muted badge-title">Employee ID</small>
                                <div className="fw-bold" style={{ fontFamily: 'monospace', fontSize: 13 }}>
                                    {emp.id ? (emp.id.length > 8 ? `••••${emp.id.slice(-6)}` : emp.id) : '—'}
                                </div>
                            </Col>
                            <Col xs="auto" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <img
                                    src={edit}
                                    alt="Edit"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => navigate(
                                        `${stringMerger(routes.EMPLOYEES.HOME, routes.EMPLOYEES.ADD_EDIT_EMPLOYEE)}?id=${emp.id}`
                                    )}
                                />
                                {!emp.terminationDate && (
                                    <button
                                        onClick={() => setShowTermModal(true)}
                                        style={{
                                            fontSize: 12, fontWeight: 700, padding: '5px 14px',
                                            background: '#fef2f2', color: '#dc2626',
                                            border: '1.5px solid #fca5a5', borderRadius: 20,
                                            cursor: 'pointer', whiteSpace: 'nowrap',
                                        }}
                                        title="Open guided offboarding wizard"
                                    >
                                        ⊘ Terminate
                                    </button>
                                )}
                                {emp.terminationDate && (
                                    <span style={{
                                        fontSize: 12, fontWeight: 700, padding: '5px 14px',
                                        background: '#f3f4f6', color: '#6b7280',
                                        border: '1px solid #e5e7eb', borderRadius: 20,
                                    }}>
                                        Terminated {formatDate(emp.terminationDate)}
                                    </span>
                                )}
                            </Col>
                        </Row>

                        <Row className="mt-4">
                            <Col className='d-flex flex-column col-lg-3'>
                                <span className='badge-title'>Onboard Status</span>
                                <span className={`badge ${['Approved', 'Completed'].includes(onboardStatus) ? 'status-completed' : onboardStatus === 'Rejected' ? 'status-no-user' : onboardStatus === '—' ? 'status-no-user' : 'status-active'}`}>
                                    {onboardStatus}
                                </span>
                            </Col>
                            <Col className='d-flex flex-column col-lg-3'>
                                <span className='badge-title'>Active Status</span>
                                <span className={`badge ${emp.active ? 'status-completed' : 'status-no-user'}`}>
                                    {activeStatus}
                                </span>
                            </Col>
                            <Col className='d-flex flex-column col-lg-3'>
                                <span className='badge-title'>Payment Portal</span>
                                <span className={`badge ${emp.paymentPortalStatus === 'Active' ? 'status-completed' : emp.paymentPortalStatus === 'Inactive' ? 'status-active' : 'status-no-user'}`}>
                                    {emp.paymentPortalStatus || 'No User'}
                                </span>
                            </Col>
                        </Row>

                        <hr />
                        <h6 className="fw-bold">General Information</h6>
                        <Row>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Full Name</small><br />
                                <span className='badge-value badge-title'>{fullName}</span>
                            </Col>
                            {emp.preferredName && (
                                <Col className='col-lg-3'>
                                    <small className="text-muted badge-title">Preferred Name</small><br />
                                    <span className='badge-value badge-title'>{emp.preferredName}</span>
                                </Col>
                            )}
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Payment Method</small><br />
                                <span className='badge-value badge-title'>{empBankAccounts.length > 0 ? 'Direct Deposit' : (emp.defaultPaymentMethod || emp.paymentMethod || 'Check').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">SSN</small><br />
                                <span className='badge-value badge-title'>{maskSSN(emp.ssn)}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Date of Birth</small><br />
                                <span className='badge-value badge-title'>{formatDate(emp.dateOfBirth || emp.dob)}</span>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Job Title</small><br />
                                <span className='badge-value badge-title'>{emp.jobTitle || '—'}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Department</small><br />
                                <span className='badge-value badge-title'>{emp.department || '—'}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Employment Type</small><br />
                                <span className='badge-value badge-title'>{emp.employmentType || '—'}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Marital Status</small><br />
                                <span className='badge-value badge-title'>{emp.maritalStatus || '—'}</span>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Start Date</small><br />
                                <span className='badge-value badge-title'>{formatDate(emp.startDate || emp.hireDate)}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Termination Date</small><br />
                                <span className='badge-value badge-title'>{formatDate(emp.terminationDate)}</span>
                            </Col>
                        </Row>

                        <hr />
                        <h6 className="fw-bold">Contact Information</h6>
                        <Row>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Address</small><br />
                                <span className='badge-value badge-title'>{residence}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Email</small><br />
                                <span className='badge-value badge-title'>{emp.email || '—'}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Home Phone</small><br />
                                <span className='badge-value badge-title'>{emp.homePhone || emp.phone || '—'}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Cell Phone</small><br />
                                <span className='badge-value badge-title'>{emp.cellPhone || '—'}</span>
                            </Col>
                        </Row>

                        {(emp.emergencyContactFirstName || emp.emergencyContactLastName || emp.emergencyContactPhone) && (<>
                        <hr />
                        <h6 className="fw-bold">Emergency Contact</h6>
                        <Row>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Name</small><br />
                                <span className='badge-value badge-title'>{[emp.emergencyContactFirstName, emp.emergencyContactLastName].filter(Boolean).join(' ') || '—'}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Relationship</small><br />
                                <span className='badge-value badge-title'>{emp.emergencyContactRelationship || '—'}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Primary Phone</small><br />
                                <span className='badge-value badge-title'>{emp.emergencyContactPhone || '—'}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Alternate Phone</small><br />
                                <span className='badge-value badge-title'>{emp.emergencyContactAltPhone || '—'}</span>
                            </Col>
                        </Row>
                        {emp.emergencyContactEmail && (
                            <Row className="mt-3">
                                <Col className='col-lg-3'>
                                    <small className="text-muted badge-title">Emergency Email</small><br />
                                    <span className='badge-value badge-title'>{emp.emergencyContactEmail}</span>
                                </Col>
                            </Row>
                        )}
                        </>)}

                        {(emp.beneficiaryName || emp.beneficiaryRelationship) && (<>
                        <hr />
                        <h6 className="fw-bold">Beneficiary Information</h6>
                        <Row>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Beneficiary Name</small><br />
                                <span className='badge-value badge-title'>{emp.beneficiaryName || '—'}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Relationship</small><br />
                                <span className='badge-value badge-title'>{emp.beneficiaryRelationship || '—'}</span>
                            </Col>
                        </Row>
                        </>)}

                        {emp.payrollNotes && (<>
                        <hr />
                        <h6 className="fw-bold">Notes for Payroll Team</h6>
                        <Row>
                            <Col>
                                <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:8, padding:'10px 14px', fontSize:13, color:'#92400e', lineHeight:1.6 }}>
                                    {emp.payrollNotes}
                                </div>
                            </Col>
                        </Row>
                        </>)}

                        <hr />
                        <h6 className="fw-bold">Tax Withholding</h6>
                        <Row>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Federal Filing Status</small><br />
                                <span className='badge-value badge-title'>{emp.federalFilingStatus || '—'}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Additional Withholding</small><br />
                                <span className='badge-value badge-title'>{emp.federalExtraWithholding != null ? `$${emp.federalExtraWithholding}` : '—'}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Federal Exempt</small><br />
                                <span className='badge-value badge-title'>{emp.federalExempt ? 'Yes' : 'No'}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">State Filing Status</small><br />
                                <span className='badge-value badge-title'>{emp.stateFilingStatus || '—'}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">State Allowances</small><br />
                                <span className='badge-value badge-title'>{emp.stateAllowances != null ? emp.stateAllowances : '—'}</span>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Multiple Jobs</small><br />
                                <span className='badge-value badge-title'>{emp.multipleJobs || '—'}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Dependents Amount</small><br />
                                <span className='badge-value badge-title'>{emp.dependentsAmount != null ? `$${emp.dependentsAmount}` : '—'}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Other Income</small><br />
                                <span className='badge-value badge-title'>{emp.otherIncome != null ? `$${emp.otherIncome}` : '—'}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Deductions</small><br />
                                <span className='badge-value badge-title'>{emp.deductionsAmount != null ? `$${emp.deductionsAmount}` : '—'}</span>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Resident State</small><br />
                                <span className='badge-value badge-title'>{emp.residentState || '—'}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Work State</small><br />
                                <span className='badge-value badge-title'>{emp.workState || '—'}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Local Tax City / County</small><br />
                                <span className='badge-value badge-title'>{emp.localTaxArea || '—'}</span>
                            </Col>
                        </Row>

                        <hr />
                        <h6 className="fw-bold">Work Eligibility & I-9</h6>
                        <Row>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">U.S. Citizen</small><br />
                                <span className='badge-value badge-title'>{emp.usCitizen || '—'}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Authorized to Work</small><br />
                                <span className='badge-value badge-title'>{emp.authorizedToWork || '—'}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Work Auth Type</small><br />
                                <span className='badge-value badge-title'>{emp.workAuthType || '—'}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Work Auth Expiry</small><br />
                                <span className='badge-value badge-title'>{formatDate(emp.workAuthExpiry)}</span>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Rehire</small><br />
                                <span className='badge-value badge-title'>{emp.rehire || '—'}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">I-9 Section 1</small><br />
                                <span className='badge-value badge-title'>{emp.i9Section1Completed || '—'}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">I-9 Date</small><br />
                                <span className='badge-value badge-title'>{formatDate(emp.i9Date)}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Workers Comp Code</small><br />
                                <span className='badge-value badge-title'>{emp.workersCompCode || '—'}</span>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Primary Work State</small><br />
                                <span className='badge-value badge-title'>{emp.primaryWorkState || '—'}</span>
                            </Col>
                            <Col className='col-lg-3'>
                                <small className="text-muted badge-title">Remote Work State</small><br />
                                <span className='badge-value badge-title'>{emp.remoteWorkState || '—'}</span>
                            </Col>
                        </Row>
                    </Bcard>
                    <Bcard className="employee-card p-3" style={{ width: '80%' }}>
                        <ul className="d-flex justify-content-between tab" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {TABS.map((tab, i) => (
                                <li
                                    key={tab}
                                    className={`nav-link${activeTab === i ? ' active' : ''}`}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => {
                                        setActiveTab(i);
                                        if (i === 1 && emp?.email) {
                                            if (empDocs === null) fetchDocs(emp.email);
                                            if (empTaxDocs === null) fetchTaxDocs(employeeId);
                                        }
                                    }}
                                    aria-current={activeTab === i ? 'page' : undefined}
                                >
                                    {tab}
                                </li>
                            ))}
                        </ul>

                        <div className="mt-3">
                            {activeTab === 0 && (
                                payrollsChecking
                                    ? <p className="text-muted text-center py-3">
                                        <span className="spinner-border spinner-border-sm me-2" role="status" />
                                        Loading pay history...
                                      </p>
                                    : employeePayrolls.length === 0
                                        ? <p className="text-muted text-center">No paystubs available.</p>
                                        : <table className="table table-sm table-hover" style={{ fontSize: 13 }}>
                                            <thead style={{ background: '#f8f9fa' }}>
                                                <tr><th>Period</th><th>Pay Date</th><th></th></tr>
                                            </thead>
                                            <tbody>
                                                {employeePayrolls.map(p => (
                                                    <tr key={p.id}>
                                                        <td>{formatDate(p.startDate)} &mdash; {formatDate(p.endDate)}</td>
                                                        <td>{formatDate(p.payDate)}</td>
                                                        <td>
                                                            <button
                                                                className="btn btn-sm"
                                                                style={{ backgroundColor: '#E7730A', color: '#fff', fontSize: 12 }}
                                                                onClick={() => setStubPayroll(p)}
                                                            >
                                                                Pay Stub
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                          </table>
                            )}

                            {activeTab === 1 && (
                                <div>
                                {/* Upload toolbar */}
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <span style={{ fontSize: 13, color: '#6b7280' }}>
                                        {empDocs ? `${empDocs.length} document${empDocs.length !== 1 ? 's' : ''}` : ''}
                                    </span>
                                    <div>
                                        <input
                                            ref={docUploadRef}
                                            type="file"
                                            accept=".pdf,.doc,.docx,.csv,.png,.jpg,.jpeg"
                                            style={{ display: 'none' }}
                                            onChange={e => { if (e.target.files?.[0]) uploadDoc(e.target.files[0]); }}
                                        />
                                        <button
                                            className="btn btn-sm"
                                            style={{ backgroundColor: '#E7730A', color: '#fff', fontWeight: 600, fontSize: 12 }}
                                            disabled={uploadingDoc}
                                            onClick={() => docUploadRef.current?.click()}
                                        >
                                            {uploadingDoc
                                                ? <><span className="spinner-border spinner-border-sm me-1" role="status" />Uploading…</>
                                                : '+ Upload Document'}
                                        </button>
                                    </div>
                                </div>

                                {docsLoading ? (
                                    <p className="text-muted text-center py-3">
                                        <span className="spinner-border spinner-border-sm me-2" role="status" />
                                        Loading documents...
                                    </p>
                                ) : empDocs === null ? (
                                    <p className="text-muted text-center">Click the Documents tab to load.</p>
                                ) : empDocs.length === 0 ? (
                                    <p className="text-muted text-center">No documents uploaded yet.</p>
                                ) : (
                                    <table className="table table-sm table-hover" style={{ fontSize: 13 }}>
                                        <thead style={{ background: '#f8f9fa' }}>
                                            <tr>
                                                <th>File Name</th>
                                                <th>Submitted</th>
                                                <th style={{ width: 120 }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {empDocs.map(doc => (
                                                <tr key={doc.id}>
                                                    <td>
                                                        <span style={{ fontSize: 15, marginRight: 6 }}>📄</span>
                                                        {doc.fileName || `Document #${doc.id}`}
                                                    </td>
                                                    <td>{new Date(doc.submittedAt).toLocaleDateString('en-US', { month:'short', day:'2-digit', year:'numeric' })}</td>
                                                    <td>
                                                        <div className="d-flex gap-2">
                                                            <a
                                                                href={`${ONBOARDING_API}${doc.fileUrl}`}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="btn btn-sm"
                                                                style={{ backgroundColor:'#E7730A', color:'#fff', fontSize:11, padding:'3px 10px' }}
                                                            >
                                                                View
                                                            </a>
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                style={{ fontSize:11, padding:'3px 10px' }}
                                                                disabled={deletingDocId === doc.id}
                                                                onClick={() => deleteDoc(doc.id)}
                                                            >
                                                                {deletingDocId === doc.id ? '…' : 'Delete'}
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}

                                {/* ── Tax Documents (W-2 / 1099) ── */}
                                <div className="mt-4">
                                    <div className="d-flex align-items-center mb-2" style={{ gap: 8 }}>
                                        <span style={{ fontSize: 14, fontWeight: 700, color: '#374151' }}>Tax Documents</span>
                                        <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 400 }}>(W-2 / 1099 — published by employer)</span>
                                    </div>
                                    {taxDocsLoading ? (
                                        <p className="text-muted text-center py-2">
                                            <span className="spinner-border spinner-border-sm me-2" role="status" />
                                            Loading tax documents...
                                        </p>
                                    ) : empTaxDocs === null ? (
                                        <p className="text-muted" style={{ fontSize: 13 }}>Click the Documents tab to load.</p>
                                    ) : empTaxDocs.length === 0 ? (
                                        <p className="text-muted" style={{ fontSize: 13 }}>No published tax documents yet.</p>
                                    ) : (
                                        <table className="table table-sm table-hover" style={{ fontSize: 13 }}>
                                            <thead style={{ background: '#f8f9fa' }}>
                                                <tr>
                                                    <th>Document</th>
                                                    <th>Year</th>
                                                    <th>Published</th>
                                                    <th style={{ width: 100 }}>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {empTaxDocs.map(doc => {
                                                    const cid = localStorage.getItem('companyId') || '';
                                                    const dlUrl = `/companies/${cid}/documents/employee_tax_documents/${doc.id}/download`;
                                                    return (
                                                        <tr key={doc.id}>
                                                            <td>
                                                                <span style={{ fontSize: 15, marginRight: 6 }}>📋</span>
                                                                {doc.label || `Tax Document #${doc.id}`}
                                                            </td>
                                                            <td>{doc.taxYear || doc.year || '—'}</td>
                                                            <td>{doc.filedOn ? new Date(doc.filedOn).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '—'}</td>
                                                            <td>
                                                                <button
                                                                    className="btn btn-sm"
                                                                    style={{ backgroundColor: '#E7730A', color: '#fff', fontSize: 11, padding: '3px 10px' }}
                                                                    onClick={async () => {
                                                                        try {
                                                                            const res = await axiosInstance.get(dlUrl, { responseType: 'blob' });
                                                                            const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                                                            const a = document.createElement('a');
                                                                            a.href = url;
                                                                            a.download = `${doc.label || 'tax-document'}.pdf`;
                                                                            document.body.appendChild(a);
                                                                            a.click();
                                                                            a.remove();
                                                                            URL.revokeObjectURL(url);
                                                                        } catch { toast('Failed to download document.', 'error'); }
                                                                    }}
                                                                >
                                                                    Download
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                                </div>
                            )}

                            {activeTab === 2 && (
                                <div>
                                    {emp.workplaceId || emp.primaryWorkplaceId
                                        ? <p className="badge-value">Workplace ID: {emp.primaryWorkplaceId || emp.workplaceId}</p>
                                        : <p className="text-muted text-center">No workplace assigned.</p>
                                    }
                                    {emp.workLocation && <p className="badge-value">Work Location: {emp.workLocation}</p>}
                                    {emp.workTimeZone && <p className="badge-value">Time Zone: {emp.workTimeZone}</p>}
                                    {emp.workFromHome && <p className="badge-value">Work From Home: {emp.workFromHome}</p>}
                                </div>
                            )}

                            {activeTab === 3 && (
                                empBenefits.length === 0
                                    ? <p className="text-muted text-center">No benefits assigned.</p>
                                    : <table className="table table-sm table-hover" style={{ fontSize: 13 }}>
                                        <thead style={{ background: '#f8f9fa' }}>
                                            <tr><th>Benefit Name</th><th>Type</th><th>Company Contribution</th><th>Employee Deduction</th></tr>
                                        </thead>
                                        <tbody>
                                            {empBenefits.map(b => {
                                                const template = companyBenefitMap[b.companyBenefitId] || companyBenefitMap[b.benefitId] || {};
                                                const name = template.name || template.benefitName || template.title || b.name || b.benefitName || 'Benefit';
                                                const type = template.type || template.benefitType || b.type || '—';
                                                return (
                                                    <tr key={b.id}>
                                                        <td className="fw-medium">{name}</td>
                                                        <td style={{ textTransform: 'capitalize' }}>{type}</td>
                                                        <td>{fmtMoney(b.companyContribution)}</td>
                                                        <td>{fmtMoney(b.employeeDeduction)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                            )}

                            {activeTab === 4 && (
                                (empMisc.length === 0 && empChild.length === 0)
                                    ? <p className="text-muted text-center">No deductions assigned.</p>
                                    : <table className="table table-sm table-hover" style={{ fontSize: 13 }}>
                                        <thead style={{ background: '#f8f9fa' }}>
                                            <tr><th>Type</th><th>Amount</th><th>Description</th></tr>
                                        </thead>
                                        <tbody>
                                            {empMisc.map(d => (
                                                <tr key={d.id}>
                                                    <td>Miscellaneous</td>
                                                    <td>{fmtMoney(d.amount)}</td>
                                                    <td>{d.description || '—'}</td>
                                                </tr>
                                            ))}
                                            {empChild.map(d => (
                                                <tr key={d.id}>
                                                    <td>Child Support</td>
                                                    <td>{fmtMoney(d.amount)}</td>
                                                    <td>{d.description || '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                            )}

                            {activeTab === 6 && (
                                <div>
                                    <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
                                        Bank accounts on file for direct deposit. Use Plaid to verify accounts for ACH disbursements via Dwolla.
                                    </p>
                                    {empBankAccounts.length === 0 ? (
                                        <p className="text-muted text-center">No bank accounts on file.</p>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                            {empBankAccounts.map((acct, idx) => (
                                                <div key={acct.id ?? idx} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 20px', background: '#fafafa' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                                                        <div>
                                                            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                                                                {acct.institutionName || 'Bank Account'}
                                                                &nbsp;
                                                                <span style={{ fontSize: 12, fontWeight: 400, color: '#6b7280', textTransform: 'capitalize' }}>
                                                                    ({acct.subtype || acct.accountType || 'checking'})
                                                                </span>
                                                            </div>
                                                            <div style={{ fontSize: 13, color: '#6b7280' }}>
                                                                ••••{String(acct.accountNumber ?? '').slice(-4)}
                                                                {acct.routingNumber && <>&nbsp;&nbsp;|&nbsp;&nbsp;Routing: {acct.routingNumber}</>}
                                                            </div>
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                            {acct.isVerified ? (
                                                                <span style={{ background: '#16a34a', color: '#fff', borderRadius: 20, padding: '4px 14px', fontSize: 12, fontWeight: 700 }}>
                                                                    Plaid Verified
                                                                </span>
                                                            ) : (
                                                                <PlaidLinkButton
                                                                    userId={employeeId}
                                                                    bankAccountId={acct.id}
                                                                    ownerType="employee"
                                                                    companyId={companyId}
                                                                    label="Verify with Plaid"
                                                                    onSuccess={() => queryClient.invalidateQueries({ queryKey: ['employee-bank-accounts', employeeId] })}
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === 5 && (
                                timeOffBalances.length === 0
                                    ? <p className="text-muted text-center">No time off balances assigned. Go to <strong>Time Off</strong> &rarr; Balances to assign.</p>
                                    : <table className="table table-sm table-hover" style={{ fontSize: 13 }}>
                                        <thead style={{ background: '#f8f9fa' }}>
                                            <tr><th>Policy</th><th>Type</th><th className="text-center">Balance</th><th className="text-center">Used</th><th className="text-center">Pending</th><th className="text-center">YTD Accrued</th></tr>
                                        </thead>
                                        <tbody>
                                            {timeOffBalances.map(b => (
                                                <tr key={b.id}>
                                                    <td className="fw-medium">{b.policy?.name ?? '—'}</td>
                                                    <td>{b.policy?.timeOffType ?? '—'}</td>
                                                    <td className="text-center fw-semibold" style={{ color: b.balanceHours > 0 ? '#198754' : '#dc3545' }}>{b.balanceHours}h</td>
                                                    <td className="text-center text-danger">{b.usedHours}h</td>
                                                    <td className="text-center" style={{ color: '#fd7e14' }}>{b.pendingHours}h</td>
                                                    <td className="text-center text-muted">{b.yearToDateAccruedHours}h</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                            )}
                            {activeTab === 7 && (
                                <I9Section2Tab emp={emp} queryClient={queryClient} />
                            )}
                        </div>
                    </Bcard>
                </div>
            </div>

            {stubPayroll && (
                <EmployeePayStubModal
                    payroll={stubPayroll}
                    employeeId={employeeId}
                    onClose={() => setStubPayroll(null)}
                    emp={emp}
                    empBenefits={empBenefits}
                    companyBenefitMap={companyBenefitMap}
                    empMisc={empMisc}
                    empChild={empChild}
                    timeOffBalances={timeOffBalances}
                    isLatestPayroll={stubPayroll.id === employeePayrolls[0]?.id}
                />
            )}

            <ConfirmModal
                show={confirmModal.show}
                title="Delete Document?"
                message="This cannot be undone."
                onConfirm={confirmDeleteDoc}
                onCancel={() => setConfirmModal({ show: false, docId: null })}
            />

            {showTermModal && emp && (
                <TerminationModal
                    emp={emp}
                    onClose={() => setShowTermModal(false)}
                    onDone={() => queryClient.invalidateQueries({ queryKey: ['employee', employeeId] })}
                />
            )}
        </div>
    );
}

export default ViewEmployees;









